import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { ImageIcon } from 'lucide-react';
import { Card } from '@shared/ui/containers/Card';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { EmptyState } from '@shared/ui/feedback/EmptyState';
import { CompactHeader } from '@shared/ui/headers/HeroHeader';
import { fetchGalleryAlbum } from './api';
import { GalleryLightbox } from './GalleryLightbox';

const MONTH_FMT = new Intl.DateTimeFormat(undefined, { month: 'long', day: 'numeric', year: 'numeric' });

function formatAlbumDate(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : MONTH_FMT.format(d);
}

/**
 * Album detail — cover header over a tiled photo grid; tap opens swipeable lightbox.
 */
export function GalleryAlbum(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const albumQuery = useQuery({
    queryKey: ['gallery-album', id],
    queryFn: () => fetchGalleryAlbum(id ?? ''),
    enabled: !!id,
  });

  if (albumQuery.isLoading) {
    return (
      <div className="min-h-full bg-paper">
        <Skeleton className="h-16 rounded-b-md" />
        <div className="grid grid-cols-2 gap-2.5 p-5">
          <Skeleton className="aspect-square rounded-xl" />
          <Skeleton className="aspect-square rounded-xl" />
          <Skeleton className="aspect-square rounded-xl" />
          <Skeleton className="aspect-square rounded-xl" />
        </div>
      </div>
    );
  }

  if (albumQuery.isError) {
    return (
      <div className="min-h-full bg-paper px-5 pt-safe-or-6">
        <ErrorState onRetry={() => albumQuery.refetch()} />
      </div>
    );
  }

  const album = albumQuery.data;
  if (!album) {
    return (
      <div className="min-h-full bg-paper px-5 pt-safe-or-6">
        <EmptyState title="Album not found." />
      </div>
    );
  }

  const dateLabel = formatAlbumDate(album.album_date ?? album.updated_at);
  const metaLabel = [dateLabel, `${album.photos.length} photos`].filter(Boolean).join(' · ');

  return (
    <div className="min-h-full bg-paper">
      <CompactHeader
        backTo="/app/gallery"
        backLabel="Back to gallery"
        backBehavior="parent"
        title={album.name}
      />

      <div className="px-5 pt-5 pb-safe-or-6">
        {(metaLabel || album.description) ? (
          <Card noGrain className="mb-4 p-4">
            {metaLabel ? (
              <p className="text-caption text-ink-muted">{metaLabel}</p>
            ) : null}
            {album.description ? (
              <p className={metaLabel ? 'mt-2 text-body text-ink-secondary' : 'text-body text-ink-secondary'}>
                {album.description}
              </p>
            ) : null}
          </Card>
        ) : null}

        {album.photos.length === 0 ? (
          <EmptyState
            icon={<ImageIcon className="h-7 w-7" strokeWidth={1.5} />}
            title="No photos yet."
            description="Photos will appear here when they are added to this album."
          />
        ) : (
          <ul className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
            {album.photos.map((photo, index) => (
              <li key={photo.photo_id}>
                <button
                  type="button"
                  onClick={() => setLightboxIndex(index)}
                  className="aspect-square w-full overflow-hidden rounded-xl bg-sunken transition-transform duration-fast active:scale-[0.98]"
                >
                  {photo.thumbnail_url || photo.image_url ? (
                    <img
                      src={photo.thumbnail_url ?? photo.image_url}
                      alt={photo.caption ?? ''}
                      loading="lazy"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-primary-50">
                      <ImageIcon className="h-7 w-7 text-primary-600" strokeWidth={1.5} />
                    </div>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <AnimatePresence>
        {lightboxIndex !== null && id ? (
          <GalleryLightbox
            albumId={id}
            photos={album.photos}
            index={lightboxIndex}
            onClose={() => setLightboxIndex(null)}
            onIndexChange={setLightboxIndex}
          />
        ) : null}
      </AnimatePresence>
    </div>
  );
}
